import { useState } from "react";
import { App } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { MessageInstance } from "antd/es/message/interface";
import type { Deposit, DepositReceiptOutcome } from "../api/deposits";
import {
  depositReceiptStateOf,
  isDepositReceiptOutcomeUnknown,
} from "../lib/depositReceipt";
import { getApiErrorMessage } from "../utils/translations";
import { blockIfLicenseReadOnly } from "../lib/licenseReadOnly";
import { useIssueDepositReceipt } from "./useDeposits";
import { usePrinterNotifications } from "./usePrinterNotifications";

type ReceiptNotifiers = {
  message: MessageInstance;
  notifyPrinterError: (text: string) => void;
  notifyPrinterTimeout: (text: string) => void;
  t: TFunction;
};

/**
 * Изходът на бона за депозит като известие — по записа на BE
 * (`deposit.fiscalStatus`), не по суровия `fiscal_result`: успех → toast;
 * неясно → кехлибарено (бонът може да е на хартия); отказ и теглена карта без
 * бон → червено (последното казва изрично да не се тегли пак).
 */
export const notifyDepositReceiptOutcome = (
  outcome: DepositReceiptOutcome,
  { message, notifyPrinterError, notifyPrinterTimeout, t }: ReceiptNotifiers,
) => {
  const { deposit } = outcome;
  const error = outcome.fiscal_result?.error || t("depositReceipt.noDetails");
  const params = {
    id: deposit.id,
    name: deposit.name,
    error,
    number: deposit.receiptNumber ?? "—",
  };
  switch (depositReceiptStateOf(deposit)) {
    case "ready":
      message.success(
        deposit.fiscalStatus === "not_required"
          ? t("depositReceipt.notRequired", params)
          : t("depositReceipt.issued", params),
      );
      return;
    case "unknown":
      notifyPrinterTimeout(t("depositReceipt.unknown", params));
      return;
    case "card_charged":
      notifyPrinterError(t("depositReceipt.cardCharged", params));
      return;
    default:
      notifyPrinterError(t("depositReceipt.failed", params));
  }
};

/** Категоричен отказ на BE (4xx) — нищо не е пратено към принтера. */
const httpStatusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

/**
 * „Издай бон" за депозит без бон — в избора на депозит („Закрий") и в
 * „Справка депозити" (`POST /deposits/{id}/receipt`, право `load_deposit`).
 * При неясен последен изход първо пита: бонът може вече да е излязъл, а
 * дедупът на апарата пази само на същата каса (като „Фискални проблеми").
 * `message` идва отвън — в Маркиране е `useOrderingMessage()`.
 */
export function useIssueDepositReceiptAction(message: MessageInstance) {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const { notifyPrinterError, notifyPrinterTimeout } = usePrinterNotifications();
  const issueMutation = useIssueDepositReceipt();
  const [issuingDepositId, setIssuingDepositId] = useState<number | null>(null);
  // Въпросът при неясен изход е отворен — слоят отдолу не слуша клавиши.
  const [isConfirming, setIsConfirming] = useState(false);

  const run = async (deposit: Deposit) => {
    setIssuingDepositId(deposit.id);
    try {
      const outcome = await issueMutation.mutateAsync({
        depositId: deposit.id,
        paymentMethodId: deposit.paymentMethodId,
      });
      notifyDepositReceiptOutcome(outcome, {
        message,
        notifyPrinterError,
        notifyPrinterTimeout,
        t,
      });
    } catch (error) {
      const status = httpStatusOf(error);
      if (status !== undefined && status >= 400 && status < 500) {
        // Отказ преди принтера (принтерът на предишния опит го няма, бонът още
        // се печата, касата без способността) или 402 — терминалът доказа, че
        // картата не е теглена, и BE е изтрил депозита (списъците се опресняват
        // в `onSettled`) — текстът е на BE.
        notifyPrinterError(getApiErrorMessage(error, t("depositReceipt.failedGeneric")));
      } else {
        // Без отговор / 5xx — бонът може да е излязъл.
        notifyPrinterTimeout(t("depositReceipt.noAnswer", { name: deposit.name }));
      }
    } finally {
      setIssuingDepositId(null);
    }
  };

  const issueReceipt = (deposit: Deposit) => {
    if (blockIfLicenseReadOnly()) return;
    if (issuingDepositId !== null || isConfirming) return;
    if (!isDepositReceiptOutcomeUnknown(deposit)) {
      void run(deposit);
      return;
    }
    setIsConfirming(true);
    modal.confirm({
      title: t("depositReceipt.confirmUnknownTitle"),
      content: t("depositReceipt.confirmUnknownBody"),
      icon: <ExclamationCircleFilled className="text-[var(--color-brand-deep-ink)]" />,
      centered: true,
      okText: t("depositReceipt.issue"),
      cancelText: t("common.cancel"),
      onOk: () => void run(deposit),
      afterClose: () => setIsConfirming(false),
    });
  };

  return { issueReceipt, issuingDepositId, isConfirming };
}
